import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
} from '@material-ui/core'

import {
  getAvailablePrinters,
  isAgentAvailable,
  printZpl,
  ZebraDevice,
} from 'common/utils/BrowserPrint'
import { notify } from 'utils/notify'

import Button from 'common/components/base/Button'
import Icon from 'common/components/base/Icon'
import Typography from 'common/components/base/Typography'
import Checkbox from 'common/components/base/forms/Checkbox'
import SelectMenu from 'common/components/base/forms/Select'
import SelectMenuItem from 'common/components/base/forms/Select/MenuItem'

import { PrinterSettings, usePrinterSettings } from './usePrinterSettings'

const TEST_LABEL_ZPL = [
  '^XA',
  '^CI28',
  '^PW812',
  '^LL400',
  '^MMP',
  '^FO24,40',
  '^A0N,120,120',
  '^FDTRUNKRS^FS',
  '^FO24,180',
  '^A0N,60,60',
  '^FDTestlabel OK^FS',
  '^XZ',
].join('\n')

// self-hosted Browser Print installers (CROCS-974): Zebra's own download
// page sits behind a request form and an aggressive WAF, so partners
// download from our public bucket instead
const BROWSER_PRINT_VERSION = 'v132489'
const DOWNLOADS_BASE_URL = `https://trunkrs-partner-downloads-${
  import.meta.env.VITE_ENV === 'production' ? 'prod' : 'dev'
}.s3.eu-west-1.amazonaws.com/zebra-browser-print/${BROWSER_PRINT_VERSION}`
const BROWSER_PRINT_WINDOWS_URL = `${DOWNLOADS_BASE_URL}/zebra-browser-print-windows-${BROWSER_PRINT_VERSION}.exe`
const BROWSER_PRINT_MAC_URL = `${DOWNLOADS_BASE_URL}/zebra-browser-print-mac-${BROWSER_PRINT_VERSION}.dmg`
// escape hatch when our bucket is unreachable
const BROWSER_PRINT_FALLBACK_URL =
  'https://www.zebra.com/us/en/support-downloads/software/printer-software/browser-print.html'

type AgentStatus = 'checking' | 'online' | 'offline'

export interface HandoverPrinterSettingsProps {
  open: boolean
  onClose: () => void
}

const HandoverPrinterSettings: React.FC<HandoverPrinterSettingsProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation()

  const [settings, updateSettings] = usePrinterSettings()
  const [agentStatus, setAgentStatus] = React.useState<AgentStatus>('checking')
  const [printers, setPrinters] = React.useState<ZebraDevice[]>([])
  const [isTestPrinting, setIsTestPrinting] = React.useState<boolean>(false)
  const [hasTestPrinted, setHasTestPrinted] = React.useState<boolean>(false)

  const refresh = React.useCallback(async () => {
    setAgentStatus('checking')
    const available = await isAgentAvailable()

    if (!available) {
      setAgentStatus('offline')
      setPrinters([])
      return
    }

    setAgentStatus('online')
    try {
      setPrinters(await getAvailablePrinters())
    } catch (error) {
      setPrinters([])
    }
  }, [])

  React.useEffect(() => {
    if (open) {
      refresh()
    }
  }, [open, refresh])

  const selectedPrinter = React.useMemo(
    () =>
      printers.find((printer) => printer.uid === settings.printerUid) ?? null,
    [printers, settings.printerUid],
  )

  const handleTestPrint = React.useCallback(async () => {
    setIsTestPrinting(true)
    try {
      await printZpl(TEST_LABEL_ZPL, selectedPrinter ?? undefined)
      setHasTestPrinted(true)
      notify('PRINTER_TEST_SENT', 'PRINTER_TEST_SENT_MSG')
    } catch (error) {
      notify('PRINTER_ERROR', 'PRINTER_ERROR_MSG', false)
    } finally {
      setIsTestPrinting(false)
    }
  }, [selectedPrinter])

  const handleToggleAutoPrint = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      updateSettings({ autoPrintEnabled: event.target.checked }),
    [updateSettings],
  )

  const handleSelectPrinter = React.useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) =>
      updateSettings({
        printerUid: (event.target.value as string) || null,
      } as Partial<PrinterSettings>),
    [updateSettings],
  )

  // setup checklist: the first unfinished step shows what to do next
  const steps = React.useMemo(
    () => [
      {
        done: agentStatus === 'online',
        title: t('PRINTER_STEP1_TITLE'),
        help: t('PRINTER_STEP1_HELP'),
      },
      {
        done: agentStatus === 'online' && printers.length > 0,
        title: t('PRINTER_STEP2_TITLE'),
        help: t('PRINTER_STEP2_HELP'),
      },
      {
        done: hasTestPrinted,
        title: t('PRINTER_STEP3_TITLE'),
        help: t('PRINTER_STEP3_HELP'),
      },
    ],
    [agentStatus, printers.length, hasTestPrinted, t],
  )
  const activeStep = steps.findIndex((step) => !step.done)

  return (
    <Dialog fullWidth maxWidth="sm" open={open} PaperProps={{ elevation: 1 }}>
      <div className="absolute top-4 right-4 h-16 w-16">
        <IconButton onClick={onClose}>
          <Icon name="close" />
        </IconButton>
      </div>
      <DialogTitle disableTypography>
        <Typography variant="h3">{t('PRINTER_SETTINGS')}</Typography>
        <Typography color="theBeast">
          {t('PRINTER_SETTINGS_SUB_DESCRIPTION')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <div className="mb-4">
          {agentStatus === 'checking' ? (
            <div className="flex items-center gap-2">
              <CircularProgress size={20} />
              <Typography>{t('PRINTER_AGENT_CHECKING')}</Typography>
            </div>
          ) : (
            steps.map((step, index) => (
              <div key={step.title} className="mb-3">
                <div className="flex items-center gap-2">
                  {step.done ? (
                    <Icon
                      name="check-circle"
                      style={{ height: 22, width: 22 }}
                    />
                  ) : (
                    <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-current text-xs">
                      {index + 1}
                    </span>
                  )}
                  <Typography
                    variant={index === activeStep ? 'textBold' : undefined}
                  >
                    {step.title}
                  </Typography>
                </div>
                {index === activeStep && (
                  <div className="mt-1 ml-8">
                    <Typography color="theBeast">{step.help}</Typography>
                    {index === 0 && (
                      <div className="mt-1 flex flex-col gap-1">
                        <a
                          className="underline"
                          href={BROWSER_PRINT_WINDOWS_URL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t('PRINTER_STEP1_DOWNLOAD_WINDOWS')}
                        </a>
                        <a
                          className="underline"
                          href={BROWSER_PRINT_MAC_URL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t('PRINTER_STEP1_DOWNLOAD_MAC')}
                        </a>
                        <Typography color="theBeast">
                          {t('PRINTER_STEP1_FALLBACK')}{' '}
                          <a
                            className="underline"
                            href={BROWSER_PRINT_FALLBACK_URL}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('PRINTER_STEP1_FALLBACK_LINK')}
                          </a>
                        </Typography>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div className="mb-4">
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.autoPrintEnabled}
                onChange={handleToggleAutoPrint}
              />
            }
            label={t('PRINTER_AUTO_PRINT')}
          />
        </div>

        <div className="mb-4">
          <SelectMenu
            fullWidth
            label={t('PRINTER_SELECT')}
            placeholder={t('PRINTER_SELECT_DEFAULT')}
            value={settings.printerUid ?? undefined}
            disabled={agentStatus !== 'online' || !printers.length}
            onChange={handleSelectPrinter}
          >
            <SelectMenuItem value="">
              {t('PRINTER_SELECT_DEFAULT')}
            </SelectMenuItem>
            {printers.map((printer) => (
              <SelectMenuItem key={printer.uid} value={printer.uid}>
                {printer.name}
              </SelectMenuItem>
            ))}
          </SelectMenu>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          color="white"
          startIcon={<Icon name="retry" />}
          onClick={refresh}
        >
          {t('PRINTER_REFRESH')}
        </Button>
        <Button
          disabled={agentStatus !== 'online' || isTestPrinting}
          startIcon={
            isTestPrinting ? (
              <CircularProgress size="24px" />
            ) : (
              <Icon name="print" fill="currentColor" />
            )
          }
          onClick={handleTestPrint}
        >
          {t('PRINTER_TEST_PRINT')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default HandoverPrinterSettings
